import React, { Fragment } from 'react';
import { TextField, Typography, Checkbox } from '@material-ui/core';

import { Currency, ManageNumericField } from 'react-frontend-utils' 
import { Global } from '../models/Global'
import { DateConversions } from '../utils/DateConversions';

export class CommonPatronFunctions {


    // Returns a cost item with a label and a description
    static costItem(cost, label, description) {
        return (
            <div style={{display: 'flex', justifyContent: 'left', alignItems: 'center', marginTop: 15, marginBottom: 10}}>
                <TextField label={label} variant="outlined" 
                        value={Currency.round(cost)} 
                        InputLabelProps={{ shrink: true}} readOnly={true}
                        InputProps={{inputProps: {style: {textAlign: 'right'} }}} 
                />
                <Typography variant="body2" style={{marginLeft: 10, fontStyle: 'italic', color: 'gray'}}>{description}</Typography>
            </div>
        );
    }

    static calculateServiceFee(purchaseTotal, serviceFeePercent, serviceFeeFixed, agsFees) {

        if (purchaseTotal === 0)        //No fees for free items
            return 0;
 
        // These are the client's service fees
        const serviceFee1 = purchaseTotal * (serviceFeePercent / 100.0) + serviceFeeFixed;
        const totalAfterServiceFee1 = purchaseTotal + serviceFee1;

        const agsFixed = agsFees ? agsFees.serviceFeeFixed : 0;
        const agsScale = agsFees ? agsFees.serviceFeeMultiplier : 0;

        // Special formula that computes our fee that also includes our fee
        const agsFee = ((totalAfterServiceFee1 + agsFixed)/(1 - agsScale)) - totalAfterServiceFee1;

        return serviceFee1 + agsFee;
    }


    static finalBookingSignupDateDisplay(slot, inline = false) {
        
        const start = slot.startDateTimeStr();
        const end = slot.endDateTimeStr();

        let dateLine1Display;
        let dateLine2Display;
        if (start.dateStr === end.dateStr) {     // for same day events, just show the start date and time range
            dateLine1Display = start.dateStr;
            dateLine2Display = start.timeStr + " - " + end.timeStr;
        }
        else if (slot.isAllDay()) {      // for all day events, show just the date
            dateLine1Display = start.dateStr;
        }
        else if (slot.isMultiDay()) {      // for multi-day events that are a whole number of days, show the start and end date without times
            const lastDay = DateConversions.dateStr(slot.end.minus({minutes: 1}));     // subtract 1 ms from the end time to get the correct end date string, since an event that ends at midnight technically ends on the next day;
            dateLine1Display = start.dateStr + " - " + lastDay;
        }
        else {      // for multi-day events, show the start and end date and time}
            dateLine1Display = "Begins: " + start.dateStr + " " + start.timeStr;
            dateLine2Display = "Ends: " + end.dateStr + " " + end.timeStr;
        }

        if (inline) {
            return (
                <div style={{display: 'flex', justifyContent: 'flex-start', gap: 10, alignItems: 'center'}}>
                    <Typography variant="body2" align='right' style={{marginLeft: 'auto', minWidth: 120}}>{dateLine1Display}</Typography>
                    <Typography variant="body2" align='center' style={{marginLeft: 'auto', minWidth: 120}}>{dateLine2Display}</Typography>
                </div>
            );
        }

        return (
            <Fragment>
                <Typography align='center' variant="h6">{dateLine1Display}</Typography>
                <Typography align='center' variant="h6" style={{marginBottom: 10}}>{dateLine2Display}</Typography>
                <Typography align='center' variant="body2" style={{marginBottom: 10}}>{"Timezone: " + Global.getTimezone()}</Typography>
            </Fragment>
        );
    }
    
    static multidayAdditionalSessionInfo(master, multiDaySlaves) {

        const slaveTimes = {
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            borderBottom: '1px solid gray', 
            borderTop: '1px solid gray',
            paddingBottom: 5,
            marginBottom: 20,
            paddingTop: 5,
            marginTop: 20,
        }

        // see if all the multiDaySlaves have the same location
        const allSameLocation = multiDaySlaves.every(ev => ev.locationString === master.locationString);

        return <div style={slaveTimes}>
                    <Typography variant="body2" style={{fontWeight: 'bold', marginBottom: 5}}>Additional Sessions:</Typography>
                    {multiDaySlaves.map((meSlave, index) =>
                        <div key={index} style={{display: 'flex', justifyContent: 'center', alignContent: 'center', gap: 15, width: '100%'}}>
                            <div style={{marginLeft: 'auto'}}>
                                {CommonPatronFunctions.finalBookingSignupDateDisplay(meSlave, true)}
                            </div>
                            {allSameLocation ? <div style={{marginLeft: 'auto'}}/> :
                                <Typography variant="body2" style={{fontStyle: 'italic', color: 'gray', width: '40%', marginLeft: 'auto'}}>{"Location: " + meSlave.locationString}</Typography>
                            }
                        </div>
                    )}
                </div>;
    }



    static patronNameLabel(index, count, required = false) {
        if (count === 1)
            return "Patron Name" + (required ? " *" : "");

        return "Patron " + (index + 1) + " Name" + (required ? " *" : "");
    }

    static patronNamesValid(patronNames, requireAllPatronNames) {

        if (!patronNames || patronNames.length === 0)
            return false;

        if (requireAllPatronNames)
            return patronNames.every((name) => name.trim().length > 0);
        else
            return true;
    }


    // Create an Array of the given length and fill it with objects containing selected and quantity properties
    static createOptionalCostsState(numOptionalItems) {
        return Array.from({ length: numOptionalItems }, () => ({ selected: false, quantity: 1 }));
    }

    static renderOptionalCosts(availableOptionalCosts, component, optCostArrayName, currency) {

        const itemChecked = (index, isSelected) => {

            // Create a copy of the state array to avoid mutating the original state
            const optionalCosts = [...component.state[optCostArrayName]];
            optionalCosts[index] = { selected: isSelected, quantity: isSelected ? optionalCosts[index].quantity : 1};  // If the item is deselected, reset the quantity to 1   
            component.setState({ [optCostArrayName]: optionalCosts });
        };

        const itemQuantityChanged = (index, value) => {
            const optionalCosts = [...component.state[optCostArrayName]];
            optionalCosts[index] = { ...optionalCosts[index], quantity: value };
            component.setState({[optCostArrayName]: optionalCosts});
        }

        const stateOptCostArray = component.state[optCostArrayName];
        
        return availableOptionalCosts.map((costItem, index) => 
            <Fragment key={index}>
                <div style={{display: 'flex', alignItems: 'center', marginBottom: -5, height: 60}}>
                    <Checkbox color='primary' checked={stateOptCostArray[index].selected} 
                            onChange={(event) => { itemChecked(index, event.target.checked);}}/>

                    <Typography variant="body2">{costItem.name + " (" + Currency.round(costItem.cost) + " " + currency + ")"}</Typography>

                    {stateOptCostArray[index].selected && costItem.maxQuantity > 1 ?
                        <div style={{marginLeft: 10, maxWidth: 220}}>
                            <ManageNumericField fullWidth={false} hasInfinity={false} label="Quantity" commitTimeout={10}
                                                minValue={1} maxValue={costItem.maxQuantity} initialValue={1} 
                                                autoAccept={true}
                                                changedBackgroundColor='white'
                                                json="quantity"
                                                onFieldChange={(json, value) => { itemQuantityChanged(index, value);}}/>
                        </div>
                        : null
                    }
                </div>
            </Fragment>
        );
    }


    // Common function to set the optional costs and quantities on the patron object
    // The patron is passed, along with a state array containing {selected, quantity} for each optional cost
    // This array size must match the size of the available optional costs array, which contains the name and cost of each optional cost
    static setOptionalCostsOnPatron(patron, stateOptionalCosts, availableOptionalCosts) {

        if (stateOptionalCosts.length !== availableOptionalCosts.length) {
            console.error("State optional costs array size does not match available optional costs array size");
            return;
        }

        // Clear the optional costs and quantities
        patron.optionalCosts = [];
        patron.optionalCostsQuantities = [];
        for (let i = 0; i < stateOptionalCosts.length; i++) {
            if (stateOptionalCosts[i].selected) {
                patron.optionalCosts.push(availableOptionalCosts[i].name);      // set the name from the available optional costs
                patron.optionalCostsQuantities.push(stateOptionalCosts[i].quantity);   // set the quantity from the state
            }
        }

        console.log(patron, stateOptionalCosts)
    }

    // Common function to get the total cost of the optional costs. The stateOptionalCosts array contains the selected flag and quantity for each available optional cost
    // The availableOptionalCosts array contains the name and cost of each optional cost
    // For each selected optional cost, the cost is multiplied by the selected quantity and added to the total
    static getTotalOptionalCosts(stateOptionalCosts, availableOptionalCosts) {
        return availableOptionalCosts.reduce((total, costItem, index) => {
            return total + (stateOptionalCosts[index].selected ? (costItem.cost * stateOptionalCosts[index].quantity) : 0);
        }, 0);
    }

    // Common function to get the total number of patrons added by optional costs. The stateOptionalCosts array contains the selected flag and quantity for each available optional cost
    // The availableOptionalCosts array contains the addsToPatronCount flag of each optional cost
    // For each selected optional cost that adds to patron count, the quantity is added to the total
    // If no optional costs add to patron count, null is returned, otherwise the total is returned, which may be zero
    static getTotalAddsToPatronCount(stateOptionalCosts, availableOptionalCosts) {

        const optionalCostsThatAddPatrons = availableOptionalCosts.reduce((count, costItem, index) => {
            return count + (costItem.addsToPatronCount ? 1 : 0);
        }, 0);

        if (optionalCostsThatAddPatrons === 0)
            return null;

        return availableOptionalCosts.reduce((total, costItem, index) => {
            return total + (stateOptionalCosts[index].selected && costItem.addsToPatronCount ? stateOptionalCosts[index].quantity : 0);
        }, 0);
    }

}
